import { useState } from 'react';
import { ApiError } from '../lib/api.js';
import { useLogin } from '../lib/session.js';

/**
 * Login mit Passwort oder 6-stelliger PIN (SPEC 2). Die PIN ist fuer die
 * Kinder gedacht — ein Passwort mit zwoelf Zeichen tippt niemand freiwillig
 * auf einem Smartphone.
 */
export function LoginPage() {
  const login = useLogin();
  const [mode, setMode] = useState<'password' | 'pin'>('password');
  const [identifier, setIdentifier] = useState('');
  const [secret, setSecret] = useState('');

  const error = login.error instanceof ApiError ? login.error.message : null;

  function submit(event: React.FormEvent) {
    event.preventDefault();
    login.mutate(
      mode === 'pin'
        ? { identifier: identifier.trim(), pin: secret }
        : { identifier: identifier.trim(), password: secret },
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-10">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-[var(--radius-lg)] border border-border bg-surface p-6 shadow-[var(--shadow-2)]"
      >
        <h1 className="text-xl font-semibold">Board</h1>
        <p className="mt-1 text-sm text-muted">Familien-Kanban</p>

        <div className="mt-6 flex rounded-[var(--radius)] bg-surface-muted p-1" role="tablist">
          {(['password', 'pin'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={mode === value}
              onClick={() => {
                setMode(value);
                setSecret('');
              }}
              className={`flex-1 rounded-[var(--radius-sm)] text-sm ${
                mode === value ? 'bg-surface font-medium shadow-[var(--shadow-1)]' : 'text-muted'
              }`}
              style={{ minHeight: 'var(--touch-target)' }}
            >
              {value === 'password' ? 'Passwort' : 'PIN'}
            </button>
          ))}
        </div>

        <label className="mt-4 block text-xs font-medium text-muted" htmlFor="identifier">
          Name oder E-Mail
        </label>
        <input
          id="identifier"
          name="identifier"
          autoComplete="username"
          value={identifier}
          onChange={(event) => setIdentifier(event.target.value)}
          required
          className="mt-1 w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2"
          style={{ minHeight: 'var(--touch-target)' }}
        />

        <label className="mt-4 block text-xs font-medium text-muted" htmlFor="secret">
          {mode === 'pin' ? 'PIN (6 Ziffern)' : 'Passwort'}
        </label>
        <input
          id="secret"
          name="secret"
          type="password"
          autoComplete={mode === 'pin' ? 'one-time-code' : 'current-password'}
          inputMode={mode === 'pin' ? 'numeric' : 'text'}
          pattern={mode === 'pin' ? '\\d{6}' : undefined}
          maxLength={mode === 'pin' ? 6 : undefined}
          value={secret}
          onChange={(event) => setSecret(event.target.value)}
          required
          className="mt-1 w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2"
          style={{ minHeight: 'var(--touch-target)' }}
        />

        {error && (
          <p role="alert" className="mt-4 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={login.isPending}
          className="mt-6 w-full rounded-[var(--radius)] bg-accent px-4 font-medium text-[var(--color-accent-contrast)] disabled:opacity-60"
          style={{ minHeight: 'var(--touch-target)' }}
        >
          {login.isPending ? 'Anmelden …' : 'Anmelden'}
        </button>

        <p className="mt-4 text-center text-xs text-muted">
          Zugang vergibt der Administrator. Passwort vergessen? Beim Admin melden.
        </p>
      </form>
    </div>
  );
}
